// Hooks do domínio de MARCAS: as telas pedem "as marcas" e não mais uma
// `queryKey` inline + `queryFn` montada à mão. As chaves vivem todas na factory
// abaixo, e é isso que as torna auditáveis: o React Query invalida por PREFIXO,
// então uma chave escrita em dois lugares que divergem uma letra vira dado
// velho na tela sem nenhum erro em lugar nenhum.
//
// Padrão para os próximos domínios:
//   - `src/api/<domínio>.ts` guarda o request (fetch + tipos), sem React;
//   - este arquivo guarda as CHAVES e os hooks do TanStack Query;
//   - a tela não importa a função de api: importa o hook.
//
// Um hook de mutação é dono da chamada e — quando a consequência é a mesma para
// TODO chamador — também da invalidação. Quando o efeito depende da
// orquestração da tela (trocar de tenant, navegar), ele fica na tela: esconder
// metade da ordem aqui dentro seria pior que repeti-la lá.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createBrand,
  deactivateBrand,
  getAllBrandsReport,
  getBrandDetailReport,
  getBrands,
  getMyBrands,
  joinBrand,
  purgeBrand,
  reactivateBrand,
} from "@/api/brands";
import { authClient, invalidateAppAccess } from "@/lib/auth-client";
import { switchToBrand } from "@/brand-switch";

// Recorte do relatório geral: período + ids das marcas (separados por vírgula).
// Vai inteiro na chave — recorte diferente é OUTRO relatório, não o mesmo dado.
export interface AllBrandsReportQuery {
  from?: string;
  to?: string;
  organizationIds?: string;
}

// Só período: a gaveta já fala de uma marca só.
export interface BrandReportRange {
  from?: string;
  to?: string;
}

// As chaves DESTE domínio, exatamente como já estavam espalhadas pelas telas —
// centralizar não é renomear. Duas ressalvas herdadas que valem leitura:
//
//  - `cross` (["brands"]) é PREFIXO de `mine` e de `platformProbe`: invalidar
//    ["brands"] derruba as três, e é assim que as telas contam com isso hoje.
//  - `cross` e `platformProbe` chamam o MESMO GET /v1/brands em duas entradas
//    de cache diferentes. É uma divergência pré-existente (o seletor de marca
//    usa uma, o teste de diretor de plataforma usa a outra); está preservada de
//    propósito, unificar é mudança de comportamento e fica para outra vez.
export const brandKeys = {
  // GET /v1/brands/mine. Mesma chave que o bootstrap usa em lib/auth-client.ts
  // (declarada lá à parte, para o auth-client não depender de hooks).
  mine: ["brands", "mine"] as const,
  // GET /v1/brands — listagem cross-org do seletor de marca e do relatório geral.
  cross: ["brands"] as const,
  // GET /v1/brands usada como TESTE de permissão (null = não é diretor de
  // plataforma). Ver a ressalva acima.
  platformProbe: ["brands", "all"] as const,
  allBrandsReport: (query: AllBrandsReportQuery) => ["reports", "all-brands", query] as const,
  brandDetail: (organizationId: string | undefined, range: BrandReportRange) =>
    ["reports", "brand-detail", organizationId, range] as const,
};

// Marcas em que o usuário TEM filiação (GET /v1/brands/mine). `retry: false`:
// é a lista do próprio usuário — se falhou, insistir devolveria a mesma coisa.
export function useMyBrands(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: brandKeys.mine,
    queryFn: getMyBrands,
    retry: false,
    enabled: options.enabled,
  });
}

// TODAS as marcas (GET /v1/brands). Devolve `null` — não erro — para quem não é
// diretor de plataforma; quem chama decide o que mostrar nesse caso.
export function useAllBrands(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: brandKeys.cross,
    queryFn: getBrands,
    retry: false,
    enabled: options.enabled,
  });
}

// Mesmo endpoint do hook acima, outra chave (ver brandKeys) e sem `retry:
// false`: aqui a resposta é o teste de permissão, então a política padrão de
// retry do query-client vale.
export function usePlatformDirectorBrands() {
  return useQuery({ queryKey: brandKeys.platformProbe, queryFn: getBrands });
}

// Consolidado de todas as marcas do recorte (GET /v1/reports/all-brands).
export function useAllBrandsReport(
  query: AllBrandsReportQuery,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: brandKeys.allBrandsReport(query),
    queryFn: () => getAllBrandsReport(query),
    enabled: options.enabled,
  });
}

// Detalhe de UMA marca (GET /v1/reports/all-brands/:id), sob demanda: é o
// `enabled` de quem chama que garante o id — daí o `!` no queryFn.
export function useBrandDetailReport(
  organizationId: string | undefined,
  range: BrandReportRange,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: brandKeys.brandDetail(organizationId, range),
    queryFn: () => getBrandDetailReport(organizationId!, range),
    enabled: options.enabled,
  });
}

// Reativar/apagar uma marca não muda "um dado da marca": muda o conjunto de
// marcas que o usuário alcança. Por isso o invalidate é o cache INTEIRO, sem
// argumento — restringir a ["brands"] deixaria de fora tudo que é servido
// por-org. É o que as telas já faziam à mão; aqui é uma linha só, na ordem
// original (acesso primeiro, cache depois).
function useInvalidateAfterBrandWrite() {
  const queryClient = useQueryClient();
  return async () => {
    invalidateAppAccess();
    await queryClient.invalidateQueries();
  };
}

// Os `mutationFn` abaixo chamam a função de api dentro de uma seta, em vez de
// passá-la direta: o TanStack invoca o mutationFn com (variáveis, contexto), e
// uma função de api que aceite um segundo parâmetro opcional receberia o
// contexto da mutação como se fosse o dado dela.
export function useReactivateBrand() {
  const invalidate = useInvalidateAfterBrandWrite();
  return useMutation({
    mutationFn: (organizationId: string) => reactivateBrand(organizationId),
    onSuccess: invalidate,
  });
}

export function usePurgeBrand() {
  const invalidate = useInvalidateAfterBrandWrite();
  return useMutation({
    mutationFn: (organizationId: string) => purgeBrand(organizationId),
    onSuccess: invalidate,
  });
}

// Sem invalidação de propósito: criar marca é seguido de TROCAR para ela, e
// recarregar o cache no meio traria tudo de novo com o tenant velho. Quem
// orquestra a sequência é a tela.
export function useCreateBrand() {
  return useMutation({ mutationFn: (name: string) => createBrand(name) });
}

// Trocar de marca: se ainda não há filiação, cria (join) antes de ativar — a
// regra mora em brand-switch.ts, testada sem rede. O hook existe para as telas
// não repetirem a injeção de `joinBrand` + `authClient.organization.setActive`.
// Também não invalida: a ordem entre recarregar a org ativa e limpar o cache
// difere entre criar e selecionar, e essa ordem é da tela.
export function useSwitchToBrand() {
  return useMutation({
    mutationFn: (brand: { id: string; isMember: boolean }) =>
      switchToBrand(brand, {
        joinBrand,
        setActive: (id) => authClient.organization.setActive({ organizationId: id }),
      }),
  });
}

// Desativação (DELETE /v1/brands/:id). Sem efeito de cache aqui: quem chama
// perde o próprio tenant e sai da tela — o que vem depois é `queryClient.clear()`
// e navegação, decisão da tela e não do domínio.
export function useDeactivateBrand() {
  return useMutation({ mutationFn: (organizationId: string) => deactivateBrand(organizationId) });
}
